import { useState, useEffect, useRef } from 'react';
import { Search, X, FileText, Users, Truck, ChevronRight } from 'lucide-react';
import api from '../utils/api';
import { StatusBadge } from './UI';

const TYPE_CONFIG = {
  request: { icon: FileText, color: '#F59E0B', label: 'Solicitud', section: 'requests' },
  client: { icon: Users, color: '#3B82F6', label: 'Cliente', section: 'clients' },
  professional: { icon: Users, color: '#10B981', label: 'Profesional', section: 'professionals' },
};

export default function GlobalSearch({ onNavigate }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') { setOpen(false); setQuery(''); setResults([]); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      api.adminSearch(query).then(r => setResults(r.results || [])).catch(() => setResults([])).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const handleSelect = (item) => {
    setOpen(false); setQuery(''); setResults([]);
    if (onNavigate) onNavigate(TYPE_CONFIG[item.type]?.section || 'dashboard');
  };

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 80 }}>
      <div onClick={() => { setOpen(false); setQuery(''); }} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }} />
      <div style={{ position: 'relative', width: '100%', maxWidth: 540, background: 'var(--card)', borderRadius: 16, border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
        {/* Input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
          <Search size={18} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />
          <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)} placeholder="Buscar solicitudes, clientes, profesionales..."
            style={{ flex: 1, border: 'none', background: 'none', outline: 'none', fontSize: 15, color: 'var(--text)', padding: 0 }} />
          {query && <button onClick={() => { setQuery(''); setResults([]); }} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', padding: 2 }}><X size={16} /></button>}
          <kbd style={{ padding: '2px 6px', borderRadius: 4, background: 'var(--surface-alt)', border: '1px solid var(--border)', fontSize: 10, color: 'var(--text-dim)' }}>ESC</kbd>
        </div>

        {/* Results */}
        <div style={{ maxHeight: 360, overflow: 'auto' }}>
          {loading && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Buscando...</div>}
          {!loading && query.length >= 2 && results.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Sin resultados para "{query}"</div>
          )}
          {!loading && results.map((item, i) => {
            const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.request;
            const Icon = cfg.icon;
            return (
              <button key={`${item.type}-${item.id}-${i}`} onClick={() => handleSelect(item)} style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '10px 16px',
                background: 'none', border: 'none', borderBottom: '1px solid var(--border)', cursor: 'pointer',
                textAlign: 'left', color: 'var(--text)', transition: 'background 0.1s',
              }} onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-alt)'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: `${cfg.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={15} color={cfg.color} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{cfg.label} · {item.subtitle || ''}</div>
                </div>
                {item.status && <StatusBadge status={item.status} />}
                <ChevronRight size={14} style={{ color: 'var(--text-dim)' }} />
              </button>
            );
          })}
        </div>

        {/* Footer */}
        {query.length < 2 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 12, justifyContent: 'center', fontSize: 11, color: 'var(--text-dim)' }}>
            <span><kbd style={{ padding: '1px 4px', borderRadius: 3, background: 'var(--surface-alt)', border: '1px solid var(--border)' }}>⌘K</kbd> abrir</span>
            <span><kbd style={{ padding: '1px 4px', borderRadius: 3, background: 'var(--surface-alt)', border: '1px solid var(--border)' }}>↑↓</kbd> navegar</span>
            <span><kbd style={{ padding: '1px 4px', borderRadius: 3, background: 'var(--surface-alt)', border: '1px solid var(--border)' }}>↵</kbd> seleccionar</span>
          </div>
        )}
      </div>
    </div>
  );
}
